// helper to delay async returns for devtesting

export default function delay(milliseconds: number): Promise<number> {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(milliseconds);
        }, milliseconds);
    });
}
export class AbortableDelay {
    timeoutId: number = 0;

    private startTime: Date | null = null; // When delay is started
    private reject: ((reason: AbortedException) => void) | null = null;
    private resolve: ((value: boolean) => void) | null = null;

    abort() {
        if(this.timeoutId > 0) {
            clearTimeout(this.timeoutId);
            this.timeoutId = 0;
            if(this.reject != null) {
                this.reject(new AbortedException(this.delayedMillis))
            } else if(this.resolve != null) {
                this.resolve(false);
            }
        }
    }

    get delayedMillis(): number {
        const nowMillis = (new Date()).getTime();
        if(this.startTime != null) {
            return nowMillis - this.startTime.getTime();
        }
        return 0;
    }

    /**
     * Returns a promise that without further action resolved with value true after given number of milliseconds has passed since call time.
     * If abort() is called on the AbortableDelay instance before delay promise has resolved it is either resolved immediately with value false, or a AbortedException is thrown.
     * The abort behaviour is determined by the exceptionalAbort argument.
     * If delay is called again on the same AbortableDelay instance before previous delay promise is complete, the previous delay is aborted.
     *
     * @param milliseconds How many milliseconds to delay before resolving promise.
     * @param exceptionalAbort set to true to make the abort() method reject returned promise immediately with a AbortedException. If false, the promise is resolved with false instead.
     */
    delay(milliseconds: number, exceptionalAbort: boolean): Promise<boolean> {
        // If a previous abortable delay is set, abort it now.
        this.abort();

        this.startTime = new Date();
        return new Promise((resolve, reject) => {
            if(exceptionalAbort) {
                this.reject = reject;
            } else {
                this.resolve = resolve;
            }
            this.timeoutId = setTimeout(() => {
                this.reject = null;
                this.resolve = null;
                resolve(true);
            }, milliseconds);
        });
    }
}

export class AbortedException extends Error {
    waitedMillis: number;

    constructor(waitedMillis: number) {
        super(`AbortableDelay aborted after ${waitedMillis/1000} seconds`);
        this.waitedMillis = waitedMillis;
    }
}